/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect, useRef } from 'react'
import { FormikProps } from 'formik'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Lightbox } from '@nexigendigital/nxui'
import { useChangeEmailAddressMutation } from 'api/account'
import { setUser } from 'store/slices/appSlice'
import { useAppDispatch } from 'store/hooks'
import { ChangeEmailAddress } from 'models/account'

import ChangeEmailAddressForm from 'containers/account/form/changeEmailAddressForm'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    status: boolean,
    close: () => void
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function ChangeEmailAddressLightbox({ status, close }: Props) {
    const dispatch = useAppDispatch()
    const formRef = useRef<FormikProps<ChangeEmailAddress>>(null)
    const [, { isLoading, isSuccess, isError, data }] = useChangeEmailAddressMutation({ fixedCacheKey: 'change-email-address' })

    /**********************************************************************************************************
     *   SIDE EFFECTS
     **********************************************************************************************************/
    useEffect(() => {
        if (isSuccess) {
            dispatch(setUser(data))
            close()
        }

        if (isError && formRef.current) {
            formRef.current.setSubmitting(false)
            formRef.current.resetForm()
        }
    }, [isSuccess, isError, data])

    return (
        <Lightbox
            title={'Change Email Address'}
            content={<ChangeEmailAddressForm form={formRef}/>}
            conditions={{ state: status, action: () => close() }}
            links={[]}
            actions={[
                {
                    label: 'Cancel',
                    color: 'secondary',
                    func: () => close()
                },
                {
                    label: 'Update',
                    color: 'primary',
                    loading: isLoading ? 'Updating' : false,
                    type: 'submit',
                    form: 'changeEmailAddressForm'
                }
            ]}
        />
    )
}
